/**
 * Responsibility: take a photograph of a card, framed well enough to measure.
 *
 * **Two capture paths, because one cannot do both jobs.** iOS Safari caps
 * `getUserMedia` at about 1920 on the long edge. A card filling that frame is
 * roughly 12 pixels per millimetre - plenty to *identify* a card, and short of
 * the 20 that corners need and the 24 that surface needs. The native camera,
 * reached through a file input, returns the full sensor image at about 45.
 *
 * So: the live preview does the coaching, and grading hands off to the native
 * camera for the shot. Identification stays on the live stream, where instant
 * beats sharp.
 *
 * **The overlay is the reason the live view exists.** A card photographed at a
 * tilt cannot be measured for centering, and a blurred one cannot be measured
 * at all. Showing the frame to fill, and refusing to look enthusiastic until
 * the picture is steady, costs one component and saves a round trip to a
 * two-core box that was always going to reject it.
 *
 * Blur is measured here rather than on the server for the same reason: a phone
 * can tell you in 40 ms what a 12-megapixel upload would tell you in twenty
 * seconds.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { newMemory, readFrame } from './frameStats';
import { useAutoCapture } from './useAutoCapture';

/** The aspect of every trading card: 63.5 x 88.9 mm. */
export const CARD_ASPECT = 88.9 / 63.5;

/**
 * Laplacian variance below which a frame is too soft to measure.
 *
 * Calibrated to be forgiving: a false "hold steady" on a usable frame is more
 * annoying than an occasional soft upload, and the server reports resolution
 * anyway.
 */
const SHARP_ENOUGH = 55;

export type CaptureMode = 'live' | 'native';

export interface CaptureGuide {
  /** What to line the card up with. */
  shape: 'card' | 'corner' | 'edge';
  title: string;
  instruction: string;
}

interface Props {
  guide: CaptureGuide;
  /** `native` opens the phone's own camera, for full sensor resolution. */
  mode: CaptureMode;
  /**
   * `byItself` is true when the camera decided to shoot rather than a
   * person. What is in the frame differs: a person aims at a card, and
   * an automatic shot can catch the desk between two of them.
   */
  onCapture: (file: File, byItself?: boolean) => void;
  busy?: boolean;
  /**
   * Take the shot without being asked, once the frame settles.
   *
   * For identification, where the job is a hundred cards in a row and
   * both hands are holding one. Grading captures stay manual: those are
   * ten framed shots of one card and the person is aiming each of them.
   */
  auto?: boolean;
}

export function CameraCapture({
  guide,
  mode,
  onCapture,
  busy = false,
  auto = false,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const workRef = useRef<HTMLCanvasElement>(document.createElement('canvas'));
  const [ready, setReady] = useState(false);
  const [sharp, setSharp] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);
  const shooter = useAutoCapture(SHARP_ENOUGH);
  // Read inside the timer, so a scan finishing does not restart the
  // sampling loop - and so the loop always sees the current answer
  // rather than the one captured when it was created.
  const busyRef = useRef(busy);
  busyRef.current = busy;
  // Outlives the sampling loop on purpose: it is the previous frame, and
  // a loop that restarts must not forget what it was comparing against.
  const memoryRef = useRef(newMemory());

  useEffect(() => {
    if (mode !== 'live') return undefined;
    let stream: MediaStream | null = null;
    let cancelled = false;

    navigator.mediaDevices
      // Ask for more than Safari will give. It clamps rather than failing, and
      // an Android device that can do 4K should.
      .getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 3840 },
          height: { ideal: 2160 },
        },
        audio: false,
      })
      .then((got) => {
        if (cancelled) {
          got.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = got;
        if (videoRef.current) {
          videoRef.current.srcObject = got;
          setReady(true);
        }
      })
      .catch(() => setProblem('No camera available. Use "Choose photo" instead.'));

    return () => {
      cancelled = true;
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [mode]);

  const grab = useCallback(
    (byItself = false) => {
      const video = videoRef.current;
      if (!video || !video.videoWidth) return;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d')?.drawImage(video, 0, 0);
      canvas.toBlob(
        (blob) => {
          if (blob) onCapture(new File([blob], 'capture.jpg', { type: 'image/jpeg' }), byItself);
        },
        'image/jpeg',
        0.95,
      );
    },
    [onCapture],
  );

  // One timer reads the frame, and everything on screen and every
  // decision comes off that single read. Sampling twice would double the
  // one expensive operation in the preview - drawing to a canvas and
  // reading the pixels back.
  useEffect(() => {
    if (mode !== 'live' || !ready) return undefined;
    const timer = window.setInterval(() => {
      const video = videoRef.current;
      if (!video || !video.videoWidth) return;
      const stats = readFrame(workRef.current, video, memoryRef.current);
      setSharp(stats.sharpness);
      if (auto && shooter.offer(stats, !busyRef.current)) grab(true);
    }, 350);
    return () => window.clearInterval(timer);
  }, [mode, ready, auto, shooter, grab]);

  const steady = sharp >= SHARP_ENOUGH;

  return (
    <Stack spacing={1.5}>
      <Box>
        <Typography variant="subtitle1">{guide.title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {guide.instruction}
        </Typography>
      </Box>

      {mode === 'live' && !problem && (
        <Box
          sx={{
            position: 'relative',
            bgcolor: '#000',
            borderRadius: 2,
            overflow: 'hidden',
            aspectRatio: '3 / 4',
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            {guide.shape === 'card' && (
              <Box
                sx={{
                  width: '76%',
                  aspectRatio: `1 / ${CARD_ASPECT}`,
                  border: '3px solid',
                  borderColor: steady ? 'success.main' : 'warning.main',
                  borderRadius: '4%',
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
                  transition: 'border-color 160ms',
                }}
              />
            )}
            {guide.shape === 'corner' && (
              <Box
                sx={{
                  width: '58%',
                  aspectRatio: '1 / 1',
                  borderTop: '3px solid',
                  borderLeft: '3px solid',
                  borderColor: steady ? 'success.main' : 'warning.main',
                  borderTopLeftRadius: '22%',
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
                }}
              />
            )}
            {guide.shape === 'edge' && (
              <Box
                sx={{
                  width: '82%',
                  height: '14%',
                  border: '3px solid',
                  borderColor: steady ? 'success.main' : 'warning.main',
                  borderRadius: 1,
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
                }}
              />
            )}
          </Box>
          <Chip
            size="small"
            color={steady ? 'success' : 'warning'}
            label={steady ? 'Sharp' : 'Hold steady'}
            sx={{ position: 'absolute', top: 10, left: 10 }}
          />
        </Box>
      )}

      {problem && (
        <Typography variant="body2" color="warning.main">
          {problem}
        </Typography>
      )}

      <Stack direction="row" spacing={1}>
        {mode === 'live' && !problem && (
          <Button
            variant="contained"
            startIcon={<CameraAltIcon />}
            disabled={busy || !ready}
            onClick={() => grab()}
            fullWidth
          >
            Capture
          </Button>
        )}
        <Button
          variant={mode === 'native' ? 'contained' : 'outlined'}
          component="label"
          startIcon={<PhotoLibraryIcon />}
          disabled={busy}
          fullWidth
        >
          {mode === 'native' ? 'Take photo' : 'Choose photo'}
          <input
            hidden
            type="file"
            accept="image/*"
            // Opens the phone's own camera, which returns the full sensor
            // image rather than a video frame. This is the only way to reach
            // the resolution corner and surface work need on an iPhone.
            {...(mode === 'native' ? { capture: 'environment' as const } : {})}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onCapture(file);
              event.target.value = '';
            }}
          />
        </Button>
      </Stack>
    </Stack>
  );
}
